import { useState, useMemo } from "react";
import axios from "axios";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Search,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  Users,
  History,
  Pencil,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourtesyExcusals() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const isAdminOrAP =
    user?.role === "admin" ||
    (user?.role === "user" && user?.user_type === "assistant_principal");

  const { data: students = [], isLoading } = useQuery({
    queryKey: ["students"],
    queryFn: () => base44.entities.Student.list(),
    enabled: !!user && isAdminOrAP,
  });

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const name = `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase();
      return (
        name.includes(q) ||
        (s.student_id || "").toLowerCase().includes(q) ||
        (s.email || "").toLowerCase().includes(q) ||
        (s.grade_level || "").toString().toLowerCase().includes(q)
      );
    });
  }, [students, searchTerm]);

  const applyCourtesyMutation = useMutation({
    mutationFn: async (student) => {
      // 1. Find the student's current active EA roster entry (if any) and mark it E.
      //    Student IDs are stored inconsistently between the students and
      //    roster_entries collections (leading zeros differ, e.g. '85031' vs
      //    '085031'), so normalize both sides before comparing — same pattern
      //    used in Attendance.jsx scanPresentMutation and DrivelineCheck.jsx.
      const normalizeId = (id) =>
        (id || "").toString().trim().replace(/^0+/, "") || "0";
      const studentIdNorm = normalizeId(student.student_id);

      const allEntries = await base44.entities.RosterEntry.list();
      const activeEntries = allEntries.filter((e) => {
        const entryKey = normalizeId(e.other_id || e.student_id);
        return (
          entryKey === studentIdNorm &&
          (e.status || "").toLowerCase() === "active"
        );
      });

      if (activeEntries.length > 0) {
        // Mark the most recent active entry as excused (top-level status='excused'
        // matches the manual-E path that closeout archives.)
        const target = [...activeEntries].sort((a, b) => {
          const da = new Date(a.created_date || 0).getTime();
          const db = new Date(b.created_date || 0).getTime();
          return db - da;
        })[0];
        await base44.entities.RosterEntry.update(target.id, {
          status: "excused",
        });
      }

      // 2. Record a courtesy-excusal document so it appears in reporting.
      await base44.entities.ExcusalSubmission.create({
        other_id: student.student_id,
        student_email: student.email,
        student_name: `${student.first_name || ""} ${student.last_name || ""}`.trim(),
        teacher_name: null,
        teacher_email: null,
        subject: "Courtesy excusal (direct)",
        submission_date: format(new Date(), "yyyy-MM-dd"),
        reviewed_date: format(new Date(), "yyyy-MM-dd"),
        reason: "Courtesy excusal applied directly by admin/AP",
        roster_entry_id: activeEntries[0]?.id || null,
        status: "approved",
        courtesy_excusal_used: true,
        teacher_notes: `Courtesy excusal applied by ${user?.email || "admin"}`,
      });

      // 3. Bump the student's counter via the dedicated backend endpoint
      //    (the generic entities router blocks Student updates).
      const nextCount = (student.courtesy_excusals_used || 0) + 1;
      await axios.patch(
        `${BACKEND_URL}/api/students/${encodeURIComponent(student.student_id)}`,
        { courtesy_excusals_used: nextCount },
        { withCredentials: true },
      );

      return { markedRoster: activeEntries.length > 0, newCount: nextCount };
    },
    onMutate: (student) => setPendingId(student.id),
    onSuccess: (res, student) => {
      queryClient.invalidateQueries(["students"]);
      queryClient.invalidateQueries(["myExcusals"]);
      queryClient.invalidateQueries(["rosterEntries"]);
      queryClient.invalidateQueries(["studentRecord"]);
      setMessage({
        type: "success",
        text:
          `Courtesy excusal applied for ${student.first_name} ${student.last_name}. ` +
          (res.markedRoster
            ? "Marked E on current roster. "
            : "No active roster entry to mark. ") +
          `Counter is now ${res.newCount}.`,
      });
      setTimeout(() => setMessage(null), 6000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({
        type: "error",
        text: typeof detail === "string" ? detail : "Failed to apply courtesy excusal.",
      });
      setTimeout(() => setMessage(null), 5000);
    },
    onSettled: () => setPendingId(null),
  });

  // ─── Existing courtesy excusal records: load + edit + delete ─────────────
  const { data: excusals = [] } = useQuery({
    queryKey: ["courtesyExcusals"],
    queryFn: async () => {
      // Load all approved excusals and keep only the courtesy ones
      const all = await base44.entities.ExcusalSubmission.list();
      return all
        .filter((e) => e.courtesy_excusal_used === true)
        .sort(
          (a, b) =>
            new Date(b.submission_date || 0).getTime() -
            new Date(a.submission_date || 0).getTime(),
        );
    },
    enabled: !!user && isAdminOrAP,
  });

  const [editingExcusal, setEditingExcusal] = useState(null);
  const [editForm, setEditForm] = useState({
    subject: "",
    reason: "",
    teacher_notes: "",
    submission_date: "",
  });

  const openEdit = (e) => {
    setEditingExcusal(e);
    setEditForm({
      subject: e.subject || "",
      reason: e.reason || "",
      teacher_notes: e.teacher_notes || "",
      submission_date: e.submission_date || "",
    });
  };

  const editMutation = useMutation({
    mutationFn: async () => {
      await base44.entities.ExcusalSubmission.update(editingExcusal.id, {
        subject: editForm.subject,
        reason: editForm.reason,
        teacher_notes: editForm.teacher_notes,
        submission_date: editForm.submission_date,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["courtesyExcusals"]);
      setEditingExcusal(null);
      setMessage({ type: "success", text: "Courtesy excusal updated." });
      setTimeout(() => setMessage(null), 4000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({
        type: "error",
        text: typeof detail === "string" ? detail : "Failed to update record.",
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (record) => {
      // 1. If this courtesy marked a specific roster entry E, revert it back to
      //    active/pending only when it is STILL status='excused' (don't clobber
      //    a subsequent manual edit).
      if (record.roster_entry_id) {
        try {
          const entry = await base44.entities.RosterEntry.get(record.roster_entry_id);
          if (entry && (entry.status || "").toLowerCase() === "excused") {
            await base44.entities.RosterEntry.update(record.roster_entry_id, {
              status: "active",
              attendance_status: entry.attendance_status || "pending",
            });
          }
        } catch (_e) {
          // Entry may have been archived by a closeout — safe to ignore.
        }
      }

      // 2. Decrement the student's counter (floor at 0).
      const student = students.find(
        (s) =>
          (s.student_id || "").toString() === (record.other_id || "").toString() ||
          s.email?.toLowerCase() === record.student_email?.toLowerCase(),
      );
      if (student) {
        const next = Math.max(0, (student.courtesy_excusals_used || 0) - 1);
        await axios.patch(
          `${BACKEND_URL}/api/students/${encodeURIComponent(student.student_id)}`,
          { courtesy_excusals_used: next },
          { withCredentials: true },
        );
      }

      // 3. Delete the excusal record.
      await base44.entities.ExcusalSubmission.delete(record.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["courtesyExcusals"]);
      queryClient.invalidateQueries(["students"]);
      queryClient.invalidateQueries(["rosterEntries"]);
      queryClient.invalidateQueries(["studentRecord"]);
      setMessage({
        type: "success",
        text: "Courtesy excusal deleted. Counter decremented and roster reverted where applicable.",
      });
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({
        type: "error",
        text: typeof detail === "string" ? detail : "Failed to delete record.",
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  if (user && !isAdminOrAP) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive" data-testid="courtesy-access-denied">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access this page. Only admins and assistant
            principals can apply courtesy excusals.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-blue-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Courtesy Excusals</h1>
            <p className="text-slate-600 mt-1">
              Apply a courtesy excusal directly for any student — no submission required.
            </p>
          </div>
        </div>

        {message && (
          <Alert
            className={
              message.type === "success"
                ? "border-green-200 bg-green-50"
                : "border-red-200 bg-red-50"
            }
          >
            {message.type === "success" ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription
              className={message.type === "success" ? "text-green-900" : "text-red-800"}
              data-testid="courtesy-message"
            >
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Students
              <Badge variant="secondary" className="ml-2">
                {filtered.length}
              </Badge>
            </CardTitle>
            <div className="mt-3 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="courtesy-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, student ID, email, or grade..."
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-slate-500">Loading students...</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No students match your search.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Student ID</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Courtesy Used</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => {
                    const used = s.courtesy_excusals_used || 0;
                    const limit = s.courtesy_excusals_limit || 3;
                    return (
                      <TableRow key={s.id} data-testid={`courtesy-row-${s.student_id}`}>
                        <TableCell className="font-medium">
                          {s.first_name} {s.last_name}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {s.student_id}
                        </TableCell>
                        <TableCell>{s.grade_level || "—"}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              used >= limit
                                ? "bg-red-100 text-red-800"
                                : "bg-slate-100 text-slate-800"
                            }
                          >
                            {used} / {limit}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            data-testid={`apply-courtesy-btn-${s.student_id}`}
                            size="sm"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Apply a courtesy excusal for ${s.first_name} ${s.last_name}? ` +
                                    "This will mark them E on the current roster and increment their courtesy counter.",
                                )
                              ) {
                                applyCourtesyMutation.mutate(s);
                              }
                            }}
                            disabled={pendingId === s.id}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {pendingId === s.id ? "Applying..." : "Apply Courtesy"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ─── Applied Courtesy Excusals — edit / delete ─── */}
        <Card className="border-none shadow-lg" data-testid="applied-courtesy-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Applied Courtesy Excusals
              <Badge variant="secondary" className="ml-2" data-testid="courtesy-record-count">
                {excusals.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {excusals.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No courtesy excusals have been applied yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {excusals.map((e) => (
                    <TableRow key={e.id} data-testid={`courtesy-record-row-${e.id}`}>
                      <TableCell className="font-medium">{e.student_name}</TableCell>
                      <TableCell>{e.subject || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {e.submission_date || "—"}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-md truncate">
                        {e.teacher_notes || e.reason || "—"}
                      </TableCell>
                      <TableCell className="text-right space-x-2 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-courtesy-${e.id}`}
                          onClick={() => openEdit(e)}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`delete-courtesy-${e.id}`}
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete this courtesy excusal for ${e.student_name}?\n\n` +
                                  "The student's roster status will revert (if still marked E), " +
                                  "and their courtesy counter will decrement by 1.",
                              )
                            ) {
                              deleteMutation.mutate(e);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit dialog */}
        <Dialog open={!!editingExcusal} onOpenChange={(open) => !open && setEditingExcusal(null)}>
          <DialogContent data-testid="edit-courtesy-dialog">
            <DialogHeader>
              <DialogTitle>Edit Courtesy Excusal</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Subject</label>
                <Input
                  data-testid="edit-subject"
                  value={editForm.subject}
                  onChange={(ev) =>
                    setEditForm((f) => ({ ...f, subject: ev.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-sm font-medium">Submission date</label>
                <Input
                  data-testid="edit-date"
                  type="date"
                  value={editForm.submission_date}
                  onChange={(ev) =>
                    setEditForm((f) => ({ ...f, submission_date: ev.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-sm font-medium">Reason</label>
                <Textarea
                  data-testid="edit-reason"
                  rows={2}
                  value={editForm.reason}
                  onChange={(ev) =>
                    setEditForm((f) => ({ ...f, reason: ev.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  data-testid="edit-notes"
                  rows={3}
                  value={editForm.teacher_notes}
                  onChange={(ev) =>
                    setEditForm((f) => ({ ...f, teacher_notes: ev.target.value }))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingExcusal(null)}>
                Cancel
              </Button>
              <Button
                data-testid="edit-save"
                onClick={() => editMutation.mutate()}
                disabled={editMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
